// src/features/settings/components/product-groups-panel/ProductGroupsPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useProductGroups } from '../../../inventory/hooks/useProductGroups';
import { useProductGroupPermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import ProductGroupCreateForm from './ProductGroupCreateForm';
import ProductGroupsGrid from './ProductGroupsGrid';

const EMPTY_NEW_GROUP = {
  code: '',
  name: '',
  description: '',
  lot_tracked: false,
  inventory_tracked: true,
  serial_tracked: false,
  hazardous: false,
  shelf_life_days: '',
};

export default function ProductGroupsPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useProductGroupPermissions();

  const {
    productGroups,
    loading,
    error,
    clearError,
    fetchProductGroups,
    createProductGroup,
    updateProductGroup,
    deleteProductGroup,
  } = useProductGroups();

  const [addFormVisible, setAddFormVisible] = useState(false);
  const [newGroup, setNewGroup] = useState(EMPTY_NEW_GROUP);
  const [createErrors, setCreateErrors] = useState({});

  useEffect(() => {
    fetchProductGroups();
  }, [fetchProductGroups]);

  // ── Create form ───────────────────────────────────────────────────────────

  const handleFormChange = useCallback((field, value) => {
    setNewGroup(prev => ({ ...prev, [field]: value }));
    setCreateErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const handleCreate = async () => {
    const errors = {};
    if (!newGroup.code.trim()) errors.code = t('settings:product_groups.validation.code_required');
    if (!newGroup.name.trim()) errors.name = t('settings:product_groups.validation.name_required');
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    const result = await createProductGroup({
      code: newGroup.code.trim(),
      name: newGroup.name.trim(),
      description: newGroup.description.trim() || null,
      lot_tracked: newGroup.lot_tracked,
      inventory_tracked: newGroup.inventory_tracked,
      serial_tracked: newGroup.serial_tracked,
      hazardous: newGroup.hazardous,
      shelf_life_days: newGroup.shelf_life_days ? parseInt(newGroup.shelf_life_days, 10) : null,
    });

    if (result.success) {
      showSuccess(t('settings:product_groups.success.created'));
      setNewGroup(EMPTY_NEW_GROUP);
      setAddFormVisible(false);
    } else {
      showError(result.error);
    }
  };

  const handleCancelCreate = useCallback(() => {
    setNewGroup(EMPTY_NEW_GROUP);
    setCreateErrors({});
    setAddFormVisible(false);
  }, []);

  // ── Inline checkbox toggle ────────────────────────────────────────────────

  const handleToggle = useCallback(async (row, field) => {
    if (!canUpdate) return;
    const result = await updateProductGroup(row.product_group_id, {
      name: row.name,
      description: row.description,
      lot_tracked: row.lot_tracked,
      inventory_tracked: row.inventory_tracked,
      serial_tracked: row.serial_tracked,
      hazardous: row.hazardous,
      shelf_life_days: row.shelf_life_days,
      active: row.active,
      [field]: !row[field],
    });
    if (!result.success) showError(result.error);
  }, [canUpdate, updateProductGroup, showError]);

  // ── Inline text/number edit ───────────────────────────────────────────────

  const processRowUpdate = useCallback(async (newRow, oldRow) => {
    if (!canUpdate) return oldRow;
    const result = await updateProductGroup(newRow.product_group_id, {
      name: newRow.name,
      description: newRow.description,
      lot_tracked: newRow.lot_tracked,
      inventory_tracked: newRow.inventory_tracked,
      serial_tracked: newRow.serial_tracked,
      hazardous: newRow.hazardous,
      shelf_life_days: newRow.shelf_life_days ? parseInt(newRow.shelf_life_days, 10) : null,
      active: newRow.active,
    });
    if (result.success) return result.data.data;
    showError(result.error);
    return oldRow;
  }, [canUpdate, updateProductGroup, showError]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('settings:product_groups.confirm.delete_title'),
      message: t('settings:product_groups.confirm.delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteProductGroup(row.product_group_id);
        if (result.success) {
          showSuccess(t('settings:product_groups.success.deleted'));
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteProductGroup, showSuccess, showError, t]);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('settings:product_groups.description')}
        </Typography>

        {/* Actions Bar */}
        <Box mb={2} display="flex" gap={1}>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddFormVisible(true)}
              disabled={addFormVisible}
            >
              {t('settings:product_groups.buttons.add')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchProductGroups()}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        <ProductGroupCreateForm
          visible={addFormVisible}
          values={newGroup}
          errors={createErrors}
          loading={loading}
          onChange={handleFormChange}
          onSubmit={handleCreate}
          onCancel={handleCancelCreate}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <ProductGroupsGrid
          productGroups={productGroups}
          loading={loading}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onToggle={handleToggle}
          onDelete={handleDelete}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(err) => showError(err.message)}
        />
      </Paper>

      <ConfirmDialog />
    </Box>
  );
}
